import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  GUEST_GUIDE_DEFAULT_LANGUAGE,
  getGuestCommissionRate,
  normalizeGuestGuideLanguage
} from '../constants/guestArtistGuide';
import './GuestHomePage.css';
import useGuestGuide from '../hooks/useGuestGuide';
import GuestGuideLoadState from '../components/GuestGuideLoadState';

const getGuidePath = (lang) => (
  lang === GUEST_GUIDE_DEFAULT_LANGUAGE ? '/guest-conditions' : `/guest-conditions/${lang}`
);

const GuestHomePage = () => {
  const { userInfo } = useContext(UserContext);
  const lang = normalizeGuestGuideLanguage(userInfo?.preferred_language, 'ko');
  const { document: guideDocument, loading, retry } = useGuestGuide(lang);
  const text = guideDocument?.text;
  const homeGuide = guideDocument?.webHomeGuide;

  // 노치 배경색 동기화 (PWA)
  useEffect(() => {
    const backgroundColor = '#fffdf8';  // 페이지 배경색과 일치
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', backgroundColor);
    }
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = backgroundColor;

    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8');
      }
      document.body.style.backgroundColor = originalBodyBg;
    };
  }, []);

  // 게스트 기간 포맷
  const formatPeriod = () => {
    const start = userInfo?.guest_profile?.guest_start_date;
    const end = userInfo?.guest_profile?.guest_end_date;
    if (!start || !end) return '-';
    return `${start.split('T')[0]} ~ ${end.split('T')[0]}`;
  };

  if (!guideDocument) return <div className="guest-home-page"><GuestGuideLoadState language={lang} loading={loading} retry={retry} /></div>;

  return (
    <div className="guest-home-page">
      <div className="guest-home-container">
        <div className="welcome-title">
          <span className="welcome-small">{text.welcomeTo}</span>
          <div className="glitch-container">
            <span className="welcome-large">GNEGNEGNE</span>
            <span className="glitch-layer glitch-white" aria-hidden="true">GNEGNEGNE</span>
            <span className="glitch-layer glitch-green1" aria-hidden="true">GNEGNEGNE</span>
            <span className="glitch-layer glitch-green2" aria-hidden="true">GNEGNEGNE</span>
            <div className="scan-bar"></div>
          </div>
        </div>

        <div className="guest-info-card">
          <div className="guest-id-row">
            <svg className="heart-icon" viewBox="0 0 24 24" fill="none" stroke="#00cc00" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span className="guest-id-text">{userInfo?.user_login_id} ({userInfo?.user_name})</span>
            <svg className="heart-icon" viewBox="0 0 24 24" fill="none" stroke="#00cc00" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </div>
          <div className="guest-info-row">
            <span className="guest-info-label">{text.period}</span>
            <span className="guest-info-value">{formatPeriod()}</span>
          </div>
          <div className="guest-info-row">
            <span className="guest-info-label">{text.commission}</span>
            <span className="guest-info-value">
              {getGuestCommissionRate(userInfo?.guest_profile?.commission_percentage, guideDocument.commissionRate)}
            </span>
          </div>
        </div>

        <section className="guest-policy-summary" aria-labelledby="guest-policy-summary-title">
          <h2 id="guest-policy-summary-title">{homeGuide.title}</h2>
          <p>{text.notice}</p>
          {!homeGuide.compact && homeGuide.sections.map(section => (
            <div className="guest-policy-section" key={section.title}>
              <h3>{section.title}</h3>
              <ul>
                {section.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
          <Link
            className="guest-policy-link"
            to={getGuidePath(lang)}
            state={{ returnTo: '/guest' }}
          >
            {text.fullGuideLink}
          </Link>
        </section>

        <p className="studio-hours">{text.openingHours}</p>
        <p className="studio-address">{guideDocument.address.replace(/,/g, '').toUpperCase()}</p>
      </div>
    </div>
  );
};

export default GuestHomePage;
